import {
  createCostCenterFormSchema,
  CreateCostCenterFormData,
  defaultCreateFormValues,
} from '@pages/cost-centers/utils';
import { ErrorDetailsDtoErrorCodeEnum, ResponseErrorDto } from '@interfaces/models';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { createCostCenter } from '@api/cost-centers';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { toast } from '@ui/components';

export const useCreateCostCenterDrawer = ({ onSuccess }: { onSuccess: () => void }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm({
    defaultValues: defaultCreateFormValues,
    resolver: zodResolver(createCostCenterFormSchema(t)),
    mode: 'onBlur',
  });

  const { mutate: createCostCenterMutation, isPending } = useMutation({
    mutationFn: createCostCenter,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-centers'] });
      queryClient.invalidateQueries({ queryKey: ['cost-center'] });
      onSuccess();
      toast.success(t('COST_CENTERS.CREATE_COST_CENTER_SUCCESS'));
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      if (error.errorCode === ErrorDetailsDtoErrorCodeEnum.COST_CENTER_NAME_ALREADY_EXISTS) {
        form.setError('name', {
          message: t(`API_ERRORS.${ErrorDetailsDtoErrorCodeEnum.COST_CENTER_NAME_ALREADY_EXISTS}`),
          type: 'custom',
        });
        return;
      }
      return toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const createCostCenterHandler = (data: CreateCostCenterFormData) => {
    createCostCenterMutation(data);
  };

  const onSubmit = form.handleSubmit(createCostCenterHandler);

  return { onSubmit, form, isPending };
};
